/**
 * Константы для страницы детального просмотра посадки
 */

/** Коды событий, влияющие на количество растений в посадке */
export const QUANTITY_AFFECTING_KINDS = new Set([
  'sold', 'written_off', 'disposed', 'partial_sale', 'partial_writeoff',
]);

/** Маппинг kind-кодов на человекочитаемые названия для старых событий без шаблона */
export const KIND_NAMES: Record<string, string> = {
  created: 'Создание посадки',
  status_changed: 'Изменение статуса',
  transplant: 'Перемещение посадки',
  relocation: 'Перемещение посадки',
  height_measurement: 'Измерение высоты растения',
  health_check: 'Проверка здоровья',
  pest_inspection: 'Проверка на вредителей',
  disease_inspection: 'Проверка на болезни',
  watering: 'Полив',
  fertilizing: 'Удобрение',
  pest_treatment: 'Обработка от вредителей',
  disease_treatment: 'Обработка от болезней',
  pruning: 'Обрезка',
  transplanting: 'Пересадка',
  harvest: 'Сбор урожая',
  state_correction: 'Исправление показателя',
  sold: 'Продажа',
  partial_sale: 'Частичная продажа',
  written_off: 'Списание',
  partial_writeoff: 'Частичное списание',
};

/** Технические свойства, скрытые из пользовательского интерфейса */
export const TECHNICAL_PROPERTY_CODES = new Set([
  'planting_event_kind',
  'quantity_affected',
  'planting_old_status',
  'planting_new_status',
  'planting_status_reason',
  'planting_location_payload',
  'transplanting',
]);

/** Метрики для графика прогноза симуляции */
export const FORECAST_METRICS = [
  { key: 'harvestStandingKg', label: 'Урожай и потери', color: '#389e0d', unit: 'кг/раст.' },
  { key: 'tempAvgC', label: 'Температура', color: '#f5222d', unit: '°C' },
  { key: 'totalDliMol', label: 'Освещение (DLI)', color: '#d48806', unit: 'mol/m²/д' },
  { key: 'astronomicalDaylightHours', label: 'Астрономический день', color: '#faad14', unit: 'ч/день' },
  { key: 'outdoorSunHours', label: 'Солнечные часы', color: '#d4b106', unit: 'ч/день' },
  { key: 'photoperiodHours', label: 'Фотопериод', color: '#ad6800', unit: 'ч/день' },
  { key: 'dimensions.height', label: 'Высота', color: '#1677ff', unit: 'см' },
  { key: 'etiolation.stemThickness', label: 'Отн. толщина стебля', color: '#722ed1', unit: 'индекс' },
  { key: 'etiolation.stress', label: 'Этиоляц. стресс', color: '#eb2f96', unit: 'индекс' },
  { key: 'dimensions.trunkDiameter', label: 'Диаметр стебля/ствола', color: '#13c2c2', unit: 'см' },
  { key: 'dimensions.crownDiameter', label: 'Диаметр кроны', color: '#2f54eb', unit: 'см' },
  { key: 'healthScore', label: 'Здоровье', color: '#52c41a', unit: 'индекс' },
  { key: 'soilMoisturePct', label: 'Влага почвы', color: '#8c8c8c', unit: '%' },
  { key: 'stressMultiplier', label: 'Стресс', color: '#fa8c16', unit: 'индекс' },
  { key: 'rotationFactor', label: 'Севооборот', color: '#a0522d', unit: 'индекс' },
  { key: 'gdd', label: 'GDD', color: '#722ed1', unit: '°C-день' },
  { key: 'currentQuantity', label: 'Кол-во растений', color: '#531dab', unit: 'шт', isBatchOnly: true },
  { key: 'stuntFactor', label: 'Повреждения (стант)', color: '#c41d7f', unit: 'индекс' },
] as const;

export type ForecastMetricKey = typeof FORECAST_METRICS[number]['key'];

/** Серии для столбчатой диаграммы ежедневного урожая */
export const HARVEST_DAILY_CHART_SERIES = [
  { key: 'harvestPotentialDailyKg', label: 'Прирост потенциала', color: '#389e0d' },
  { key: 'harvestCollectedTodayKg', label: 'Собрано за день', color: '#0958d9' },
  { key: 'harvestLostTodayKg', label: 'Потеряно за день', color: '#cf1322' },
] as const;

/** Запрашиваемые свойства для ряда временных данных симуляции */
export const TIMESERIES_PROPERTIES = [
  'ageDays', 'ageYears', 'ageDisplayUnit', 'ageDisplayValue', 'isAnnualLike',
  'tempMinC', 'tempMaxC', 'tempAvgC',
  'astronomicalDaylightHours', 'outdoorSunHours', 'cloudCover', 'solarPpfdUmol',
  'photoperiodHours', 'lampExtensionHours', 'supplementalLightHours',
  'totalDliMol', 'naturalDliMol', 'supplementalDliMol',
  'dliMinMol', 'dliTargetMol', 'photoperiodTargetHours',
  'recommendedLampHours', 'recommendedLampPpfdUmol',
  'recommendedLampPowerWm2', 'recommendedLampPowerTotalW',
  'dimensions.height', 'dimensions.trunkDiameter', 'dimensions.crownDiameter',
  'etiolation.stress', 'etiolation.stemThickness', 'etiolation.heightGrowthMultiplier',
  'healthScore', 'stressMultiplier', 'stressFactors',
  'soilMoisturePct', 'rotationFactor', 'rotationReasons', 'rotationState',
  'gdd', 'vegetationStage',
  'harvestReady', 'harvestReadinessPct',
  'harvestPotentialDailyKg', 'harvestPotentialCumulativeKg', 'harvestPotentialRemainingKg',
  'harvestProjectedTotalKg', 'harvestAvailableKg',
  'fruitSummary', 'fruitReferencePlantCount', 'harvestStandingKg',
  'fruitGreenKg', 'fruitTurningKg', 'fruitRipeKg', 'fruitOverripeKg',
  'harvestCollectedTodayKg', 'harvestCollectedCumulativeKg',
  'harvestLostTodayKg', 'harvestLostCumulativeKg',
  'lifecycleType', 'lifecyclePhase', 'lifecycleYieldMultiplier', 'lifecycleProgress',
  'bbchCode', 'bbchCodeText', 'bbchPrincipalStage', 'bbchPrincipalLabel',
  'bbchLabel', 'bbchConfidence', 'bbchCropGroup', 'bbchStatus',
  'bbchScale', 'bbchScaleVersion', 'bbchRange',
  'currentQuantity', 'isDead', 'stuntFactor',
];

/** Человекочитаемые названия фаз жизненного цикла */
export const LIFECYCLE_PHASE_LABELS: Record<string, string> = {
  germination: 'прорастание', seedling: 'всходы', vegetative: 'вегетация',
  flowering: 'цветение', fruiting: 'плодоношение', senescence: 'увядание',
  planting: 'посадка', rooting: 'укоренение', dormancy: 'покой',
  awakening: 'пробуждение', dieback: 'отмирание',
  chilling: 'покой (яровизация)', bud_break: 'распускание почек',
  fruit_set: 'завязь', fruit_development: 'рост плодов',
  ripening: 'созревание', harvest_ready: 'готов к сбору',
  dead: 'погибло', completed: 'завершено',
  not_planted: 'не высажено', transferred: 'перенесено',
};

/** Человекочитаемые названия принципиальных стадий BBCH (0..9) */
export const BBCH_PRINCIPAL_LABELS: Record<number, string> = {
  0: 'прорастание / развитие почек',
  1: 'развитие листьев',
  2: 'образование боковых побегов',
  3: 'рост стебля',
  4: 'развитие убираемых частей',
  5: 'появление соцветий',
  6: 'цветение',
  7: 'развитие плодов',
  8: 'созревание',
  9: 'старение / покой',
};

/** Человекочитаемые названия групп BBCH (cropGroup) */
export const BBCH_CROP_GROUP_LABELS: Record<string, string> = {
  annual_fruit: 'плодовая овощная (однолетник)',
  annual_root: 'корнеплод (однолетник)',
  bulb: 'луковичная',
  tuber: 'клубнеплод',
  woody_fruit: 'плодовое дерево',
  conifer: 'хвойное',
};

/** Человекочитаемые названия уровня доверия BBCH-маппинга */
export const BBCH_CONFIDENCE_LABELS: Record<string, string> = {
  canonical: 'каноническая шкала',
  approximate: 'приближённая шкала',
  custom: 'пользовательская',
};

/** Человекочитаемые названия lifecycle gate predicates */
export const LIFECYCLE_PREDICATE_LABELS: Record<string, string> = {
  ageDays: 'возраст',
  boltingPressure: 'риск стрелкования',
  calendarWindow: 'календарное окно',
  chillingHours: 'часы охлаждения',
  composite: 'составной гейт',
  crownHealth: 'здоровье кроны',
  floweringFrostDamage: 'повреждение цветения морозом',
  frostEvent: 'мороз',
  gdd: 'накопленная температура',
  minRootingDays: 'укоренение',
  phaseEdu: 'единицы развития фазы',
  photoperiod: 'фотопериод',
  pollinatorActivity: 'опылители',
  seasonalRadiation: 'сезонная радиация',
  seasonalWaterStress: 'водный стресс',
  senescencePressure: 'давление старения',
  soilTemperature: 'температура почвы',
};

/** Читаемые подписи стресс-факторов */
export const STRESS_FACTOR_LABELS: Record<string, string> = {
  temperature: 'Температура',
  climateWater: 'Климатическая влага (дождь + воздух)',
  soil: 'Почвенная влага (дефицит/переувлажнение)',
  light: 'Освещение',
  etiolation: 'Этиоляция',
  soilQuality: 'Качество почвы (pH/NPK/уплотнение)',
  soilInteraction: 'Взаимодействие культур (антагонизм)',
  rotation: 'Севооборот (утомление почвы)',
};

/** Названия болезней/вредителей */
export const DISEASE_NAMES: Record<string, string> = {
  late_blight: 'Фитофтороз', fusarium_wilt: 'Фузариоз',
  powdery_mildew: 'Мучнистая роса', downy_mildew: 'Пероноспороз',
  gray_mold: 'Серая гниль', root_rot: 'Корневая гниль',
  aphids: 'Тля', whitefly: 'Белокрылка', spider_mite: 'Паутинный клещ',
};

/** Коды вредителей (для выбора иконки) */
export const PEST_CODES = new Set(['aphids', 'whitefly', 'spider_mite']);

/** Читаемые подписи к причинам ротации */
export const ROTATION_REASON_LABELS: Record<string, string> = {
  ROTATION_FATIGUE_MILD: 'лёгкое утомление',
  ROTATION_FATIGUE_MODERATE: 'среднее утомление',
  ROTATION_FATIGUE_SEVERE: 'сильное утомление',
  ALLELOPATHY: 'аллелопатия',
  PATHOGEN_PRESSURE: 'патогены',
  SOIL_BIODIVERSITY_LOW: 'низкая биоразн.',
};
